import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
  Divider,
} from "@mui/material";
import {
  // Delete as DeleteIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  AddCircle as AddCircleIcon,
} from "@mui/icons-material";
import ActionsService from "../../services/actions.service";

/** Convert Workiz date to local ISO for datetime-local input */
const toLocalISO = (value) => {
  if (!value) return "";
  try {
    let dateObj;
    if (typeof value === "string" && value.includes(" ")) {
      // Handle "YYYY-MM-DD HH:mm:ss"
      const [datePart, timePart] = value.split(" ");
      dateObj = new Date(`${datePart}T${timePart}`);
    } else {
      dateObj = new Date(value);
    }
    if (isNaN(dateObj.getTime())) return "";
    const tzOffset = dateObj.getTimezoneOffset() * 60000;
    return new Date(dateObj - tzOffset).toISOString().slice(0, 16);
  } catch {
    return "";
  }
};

const WorkizDialogPublic = ({ open, onClose, webDetail }) => {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [editJobId, setEditJobId] = useState(null);
  const [connected, setConnected] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newJob, setNewJob] = useState({
    UUID: "",
    Email: "",
    FirstName: "",
    Address: "",
    City: "",
    State: "",
    PostalCode: "",
    JobType: "",
    JobDateTime: "",
    JobEndDateTime: "",
  });

  const userId = webDetail?.websiteDetails?.user?.id;

  const isJobFormValid = [
    "Email",
    "FirstName",
    "Address",
    "City",
    "State",
    "PostalCode",
    "JobType",
    "JobDateTime",
    "JobEndDateTime",
  ].every((key) => newJob[key]?.trim() !== "");

  /** Fetch connection, job types, and jobs */
  const fetchWorkizData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const connection = await ActionsService.WorkizgetConnection(userId);
      const isConnected = Boolean(connection?.apiKey && connection?.apiSecret);
      setConnected(isConnected);
      if (!isConnected) return;

      const [types, existingJobs] = await Promise.all([
        ActionsService.WorkizgetJobTypes(userId),
        ActionsService.WorkizlistJobs(userId),
      ]);

      setJobTypes(types || []);
      setJobs(existingJobs || []);
    } catch (err) {
      console.error("❌ Workiz fetch error:", err.message);
      setConnected(false);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  /** Handle create or update job */
  const handleSubmitJob = async () => {
    if (!userId || !isJobFormValid) {
      alert("Please fill all required fields.");
      return;
    }
    setCreating(true);

    try {
      const payload = {
        ...newJob,
        JobDateTime: new Date(newJob.JobDateTime).toISOString(),
        JobEndDateTime: new Date(newJob.JobEndDateTime).toISOString(),
      };

      if (editJobId) {
        await ActionsService.WorkizupdateJob(userId, payload);
      } else {
        await ActionsService.WorkizcreateJob(userId, payload);
      }

      await fetchWorkizData();

      setNewJob({
        UUID: "",
        Email: "",
        FirstName: "",
        Address: "",
        City: "",
        State: "",
        PostalCode: "",
        JobType: "",
        JobDateTime: "",
        JobEndDateTime: "",
      });
      setEditJobId(null);
    } catch (err) {
      console.error("❌ Job submit error:", err.message);
    } finally {
      setCreating(false);
    }
  };

  /** Edit job */
  const handleEditJob = (job) => {
    setEditJobId(job.UUID);
    setNewJob({
      UUID: job.UUID || "",
      Email: job.Email || "",
      FirstName: job.FirstName || "",
      Address: job.Address || "",
      City: job.City || "",
      State: job.State || "",
      PostalCode: job.PostalCode || "",
      JobType: job.JobType || "",
      JobDateTime: toLocalISO(job.JobDateTime),
      JobEndDateTime: toLocalISO(job.JobEndDateTime),
    });
  };

  /** Unassign user from job using CreatedBy */
  // const handleDeleteJob = async (job) => {
  //   if (!userId) return;
  //   try {
  //     if (!job.CreatedBy) {
  //       alert("Cannot unassign: CreatedBy is missing for this job.");
  //       return;
  //     }

  //     await ActionsService.WorkizdeleteJob(userId, {
  //       UUID: job.UUID,
  //       User: job.CreatedBy, // Use CreatedBy as user
  //     });

  //     await fetchWorkizData();
  //   } catch (err) {
  //     console.error("❌ Failed to unassign job:", err.message);
  //     alert(`Failed to unassign job: ${err.message}`);
  //   }
  // };

  useEffect(() => {
    if (open) fetchWorkizData();
  }, [open, fetchWorkizData]);

  const renderJobDetails = (job) => (
    <Box sx={{ mt: 0.5 }}>
      {[
        ["UUID", job.UUID],
        ["Job Type", job.JobType],
        [
          "Location",
          `${job.LocationKey || "N/A"} (ID: ${job.LocationId || "N/A"})`,
        ],
        ["Job Date Time", job.JobDateTime],
        ["Job End Date Time", job.JobEndDateTime],
        ["Created On", job.CreatedDate],
        ["Contact", `${job.Phone || "N/A"} | ${job.Email || "N/A"}`],
        [
          "Address",
          `${job.Address || ""}, ${job.City || ""}, ${job.State || ""}`,
        ],
      ].map(([label, value]) => (
        <Typography key={label} variant="body2" color="text.secondary">
          <strong>{label}:</strong> {value || "N/A"}
        </Typography>
      ))}
    </Box>
  );

  const renderJobList = () =>
    jobs.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        No jobs found.
      </Typography>
    ) : (
      <List>
        {jobs.map((job) => (
          <ListItem
            key={job.UUID}
            alignItems="flex-start"
            sx={{ border: "1px solid #eee", borderRadius: 2, mb: 1, p: 1.5 }}
            secondaryAction={
              <>
                <IconButton
                  edge="end"
                  color="primary"
                  onClick={() => handleEditJob(job)}
                >
                  <EditIcon />
                </IconButton>
                {/* <IconButton edge="end" onClick={() => handleDeleteJob(job)}>
                  <DeleteIcon />
                </IconButton> */}
              </>
            }
          >
            <ListItemText
              primary={
                <Typography variant="subtitle1" fontWeight="bold">
                  {job.UUID} — {job.JobType || "N/A"} —{" "}
                  {job.Status || "Unknown"}
                </Typography>
              }
              secondary={renderJobDetails(job)}
            />
          </ListItem>
        ))}
      </List>
    );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2 }}>
        ⚙️ Manage Workiz Jobs
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="70vh"
          >
            <CircularProgress />
          </Box>
        ) : connected ? (
          <Box sx={{ height: "70vh", overflowY: "auto" }}>
            {/* Create / Edit Job Form */}
            <Box
              sx={{
                border: "1px solid #ddd",
                borderRadius: 2,
                p: 2,
                mb: 3,
                bgcolor: "#fafafa",
              }}
            >
              <Typography variant="h6" gutterBottom>
                <AddCircleIcon
                  fontSize="small"
                  sx={{ mr: 1, color: "green" }}
                />
                {editJobId ? "Edit Job" : "Create New Job"}
              </Typography>

              <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
                {[
                  "Email",
                  "FirstName",
                  "Address",
                  "City",
                  "State",
                  "PostalCode",
                ].map((field) => (
                  <TextField
                    key={field}
                    label={field.replace(/([A-Z])/g, " $1")}
                    value={newJob[field]}
                    onChange={(e) =>
                      setNewJob({ ...newJob, [field]: e.target.value })
                    }
                    required
                  />
                ))}

                <FormControl fullWidth required>
                  <InputLabel>Job Type</InputLabel>
                  <Select
                    value={newJob.JobType}
                    label="Job Type"
                    onChange={(e) =>
                      setNewJob({ ...newJob, JobType: e.target.value })
                    }
                  >
                    {jobTypes.map((jt, idx) => (
                      <MenuItem key={idx} value={jt.Name}>
                        {jt.Name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Job Start Time"
                  type="datetime-local"
                  value={newJob.JobDateTime}
                  onChange={(e) =>
                    setNewJob({ ...newJob, JobDateTime: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                  required
                />

                <TextField
                  label="Job End Time"
                  type="datetime-local"
                  value={newJob.JobEndDateTime}
                  onChange={(e) =>
                    setNewJob({ ...newJob, JobEndDateTime: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Box>

              <Box textAlign="right" mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmitJob}
                  disabled={!isJobFormValid || creating}
                >
                  {creating
                    ? "Processing..."
                    : editJobId
                    ? "Update Job"
                    : "Create Job"}
                </Button>
                {editJobId && (
                  <Button
                    sx={{ ml: 1 }}
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setEditJobId(null);
                      setNewJob({
                        UUID: "",
                        Email: "",
                        FirstName: "",
                        Address: "",
                        City: "",
                        State: "",
                        PostalCode: "",
                        JobType: "",
                        JobDateTime: "",
                        JobEndDateTime: "",
                      });
                    }}
                  >
                    Cancel Edit
                  </Button>
                )}
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />
            {renderJobList()}
          </Box>
        ) : (
          <Typography color="error">
            Workiz is not connected for this user.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkizDialogPublic;
